import {
  IWizardDefinition,
  IWizardStepDefinition,
  IWizardActionDefinition,
  IWizardHiddenActionDefinition,
  IFormDefinition
} from '../interfaces';
import CrudEditor from '@/components/crud/Editor.vue';

interface IWizardAction {
  name: string;
  action(wizardForm, next, done, params);
}

interface IWizardHiddenAction {
  name: string;
  action(wizardForm, next, done, params);
}

interface IWizardStepRenderer {
  component;
  getProps(form);
}
class EditorStepRenderer implements IWizardStepRenderer {
  private _component;
  constructor() {
    this._component = CrudEditor;
  }
  get component() {
    return this._component;
  }
  getProps(form) {
    return {
      isEmbedded: true,
      isEdit: true,
      saveButtonPosition: 'hide',
      preventDefault: true,
      save: _ => {},
      fetchById: _ => Promise.resolve(form)
    };
  }
}

export default class WizardEngine {
  private _currentStepIndex: number;

  private _defaultRenderer;

  private _form;
  private _definition: IWizardDefinition;

  private _isDone: boolean;

  constructor(definition: IWizardDefinition) {
    this._isDone = false;
    this._currentStepIndex = 0;
    this._defaultRenderer = new EditorStepRenderer();

    this._definition = definition;
    this._form = {};
  }
  get isDone() {
    return this._isDone;
  }
  get showOverview(): boolean {
    return this._definition.showOverview !== false;
  }
  get currentStepIndex(): number {
    return this._currentStepIndex;
  }
  get steps(): IWizardStepDefinition[] {
    return this._definition.steps;
  }
  get currentStep(): IWizardStepDefinition {
    return this._definition.steps[this._currentStepIndex];
  }
  get showWizardButtons(): boolean {
    const idx = this._currentStepIndex;
    return this._definition.steps[idx].showWizardButtons;
  }
  get currentComponent() {
    const currentDefinition = this.currentDefinition;
    if (currentDefinition && currentDefinition.component) {
      return currentDefinition.component;
    }
    return this._defaultRenderer.component;
  }

  get currentProps() {
    const currentDefinition = this.currentDefinition;
    if (currentDefinition && currentDefinition.component) {
      return {};
    }
    return this._defaultRenderer.getProps(this._form);
  }

  get currentDefinition(): IFormDefinition | undefined {
    const idx = this._currentStepIndex;
    return this._definition.steps[idx].formDefinition;
  }

  get currentActions(): IWizardAction[] {
    const customActions = this.currentStep.actionDefinition;
    if (customActions) {
      Object.keys({ customActions }).map(name => this.getWizardAction(name, customActions[name]));
    }
    let defaultActions = {};
    if (this.showWizardButtons) {
      defaultActions = this.defaultActions;
    }
    return this.mergeActions(defaultActions, customActions);
  }

  get hiddenActions(): IWizardHiddenAction[] {
    const hiddenActions = this.currentStep.hiddenActionDefinition;
    if (hiddenActions) {
      return Object.keys(hiddenActions).map(name => this.getWizardHiddenAction(name, hiddenActions[name]));
    }
    return [];
  }

  get currentLabelWidth() {
    return this.currentStep.stepLabelWidth;
  }

  get hasNextStep(): boolean {
    return this._currentStepIndex < this.steps.length - 1;
  }

  get hasPrevStep(): boolean {
    return this._currentStepIndex > 0;
  }

  get form() {
    return this._form;
  }
  updateForm(form): void {
    this._form = form;
  }

  private tryPerformHook(hook) {
    return hook && hook(this._form, this.next.bind(this), this.done.bind(this));
  }
  private handleHook(hook, actionOnSuccess) {
    const result = this.tryPerformHook(hook);
    if (result && result.then) {
      result.then(_ => actionOnSuccess()).catch();
    } else if (result !== false) {
      actionOnSuccess();
    }
  }
  next(steps: number = 1): void {
    const doNext = () => {
      const newIndex = this._currentStepIndex + steps;
      if (newIndex >= 0 && newIndex <= this.steps.length - 1) {
        this._currentStepIndex = newIndex;
      }
    };

    this.handleHook(this.currentStep.beforeNext, () => {
      this.handleHook(this._definition.beforeStep, doNext);
    });
  }
  prev(): void {
    this.next(-1);
  }
  skip(): void {
    this.next();
  }
  done(): void {
    this.handleHook(this.currentStep.beforeNext, () => {
      this.handleHook(this._definition.beforeExit, () => (this._isDone = true));
    });
  }
  private get defaultActions() {
    const next: IWizardActionDefinition = {
      label: 'wizard.next',
      action(wizardForm, next) {
        next(1);
      }
    };
    const prev: IWizardActionDefinition = {
      label: 'wizard.prev',
      action(wizardForm, next) {
        next(-1);
      }
    };
    const skip: IWizardActionDefinition = {
      label: 'wizard.skip',
      action(wizardForm, next) {
        next(1);
      }
    };
    const done: IWizardActionDefinition = {
      label: 'common.ok',
      action(wizardForm, next, done) {
        done();
      }
    };
    const buttons = {};
    if (this.hasPrevStep) {
      buttons['prev'] = prev;
    }

    if (this.hasNextStep && this.currentStep.optional) {
      buttons['skip'] = skip;
    }
    if (this.hasNextStep) {
      buttons['next'] = next;
    } else {
      buttons['done'] = done;
    }
    return buttons;
  }
  private mergeActions(defaultActions, customActions) {
    if (!customActions) {
      return Object.keys(defaultActions).map(name => this.getWizardAction(name, defaultActions[name]));
    }
    const mergedActions = Object.assign(defaultActions, customActions);
    return Object.keys(mergedActions).map(name => this.getWizardAction(name, mergedActions[name]));
  }
  private getWizardAction(name: string, definition: IWizardActionDefinition) {
    const action: IWizardAction = {
      name,
      ...definition
    };
    return action;
  }
  private getWizardHiddenAction(name: string, definition: IWizardHiddenActionDefinition) {
    const hiddenAction: IWizardHiddenAction = {
      name,
      ...definition
    };
    return hiddenAction;
  }
  action(name: string, params = {}): void {
    const currentAction =
      this.currentActions.find(x => x.name === name) || this.hiddenActions.find(x => x.name === name);
    if (currentAction) {
      currentAction.action(this._form, this.next.bind(this), this.done.bind(this), params);
    }
  }
}
